/**
 * Unified Application Styles
 * All styles for the entire application are centralized here
 * This ensures consistency and makes maintenance easier
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Helper function to convert hex to rgb
export const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '0, 0, 0';
};

// ============================================================================
// BASE REUSABLE FUNCTIONS
// ============================================================================

// Базовые функции для переиспользования (определяются первыми)
const getFullWidthStyles = () => ({
    width: '100%'
});

const getMarginBottom2Styles = () => ({
    mb: 2
});

const getMarginTop1Styles = () => ({
    mt: 1
});

const getTextSecondaryColorStyles = (theme) => ({
    color: theme.palette.text.secondary
});

const getTitleMediumStyles = (theme) => ({
    fontWeight: 500,
    color: theme.palette.text.primary,
    mb: 0.5
});

const getFlexColumnGap25BaseStyles = () => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5
});

const getFlexRowGap1BaseStyles = () => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1
});

const getFlexWrapGap1BaseStyles = () => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1
});

// ============================================================================
// COMMON STYLES
// ============================================================================

export const getContainerStyles = () => ({
    py: { xs: 2, sm: 3 },
    px: { xs: 2, sm: 3 },
    pb: { xs: 10, sm: 3 }
});

export const getContainerStylesNoBottom = () => ({
    py: { xs: 2, sm: 3 },
    px: { xs: 2, sm: 3 }
});

export const getPaperStyles = (theme) => ({
    elevation: 0,
    p: { xs: 2, sm: 3 },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    bgcolor: theme.palette.background.paper,
    backgroundImage: 'none',
    position: 'relative',
    overflow: 'visible',
});

export const getPaperHoverStyles = (theme) => ({
    ...getPaperStyles(theme),
    transition: 'all 0.2s ease',
    '&:hover': {
        borderColor: theme.palette.primary.light,
        boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 12px rgba(0, 0, 0, 0.2)'
            : '0 4px 12px rgba(0, 0, 0, 0.06)',
        transform: 'translateY(-2px)'
    }
});

export const getTypographyH4Styles = (theme) => ({
    fontWeight: 600,
    color: theme.palette.text.primary,
    mb: 1,
});

export const getTypographyH5Styles = (theme) => ({
    fontWeight: 600,
    color: theme.palette.text.primary,
});

export const getTypographyH6Styles = (theme) => ({
    fontWeight: 600,
    color: theme.palette.text.primary
});

export const getLoadingContainerStyles = () => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh'
});

// ============================================================================
// CONTACTS FEED STYLES
// ============================================================================

export const getContactsFeedHeaderStyles = () => ({
    mb: 4,
    textAlign: { xs: 'left', sm: 'center' }
});

export const getContactsFeedCardStyles = (theme) => ({
    p: { xs: 2.5, sm: 3.5 },
    border: `1px solid ${theme.palette.mode === 'dark' 
        ? `rgba(${hexToRgb(theme.palette.primary.main)}, 0.25)` 
        : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.2)`}`,
    borderRadius: 3,
    bgcolor: theme.palette.background.paper,
    boxShadow: theme.palette.mode === 'dark'
        ? `0 8px 32px rgba(0, 0, 0, 0.4)`
        : `0 8px 24px rgba(${hexToRgb(theme.palette.primary.main)}, 0.08)`,
    backgroundImage: theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.08) 0%, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.05) 100%)`
        : `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.05) 0%, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.03) 100%)`,
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: theme.palette.mode === 'dark' 
            ? `0 12px 40px rgba(${hexToRgb(theme.palette.primary.main)}, 0.25)`
            : `0 12px 32px rgba(${hexToRgb(theme.palette.primary.main)}, 0.15)`,
        transform: 'translateY(-4px)'
    },
    // Декоративный элемент для оживления карточки
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '4px',
        height: '100%',
        background: `linear-gradient(to bottom, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        opacity: 0.8
    }
});

export const getContactsFeedContactNameStyles = (theme) => ({
    fontWeight: 600,
    fontSize: '1.125rem',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    '&:hover': {
        color: theme.palette.primary.main,
    }
});

export const getContactsFeedSectionTitleStyles = (theme) => ({
    fontWeight: 600,
    fontSize: '0.9375rem',
    color: theme.palette.text.primary,
    mb: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5
});

export const getContactsFeedInteractionItemStyles = (theme) => ({
    p: 2,
    borderRadius: 1,
    bgcolor: theme.palette.mode === 'dark' 
        ? `rgba(${hexToRgb(theme.palette.primary.main)}, 0.08)`
        : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.05)`,
    border: `1px solid ${theme.palette.mode === 'dark' 
        ? `rgba(${hexToRgb(theme.palette.primary.main)}, 0.15)` 
        : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.1)`}`,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    // Анимация slideInLeft отключена для предотвращения мерцания при обновлении
    '&:hover': {
        bgcolor: theme.palette.mode === 'dark'
            ? `rgba(${hexToRgb(theme.palette.primary.main)}, 0.15)`
            : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.1)`,
        borderColor: theme.palette.secondary.main,
        transform: 'translateX(8px) scale(1.01)',
        boxShadow: theme.palette.mode === 'dark'
            ? `0 4px 12px rgba(${hexToRgb(theme.palette.primary.main)}, 0.2)`
            : `0 4px 12px rgba(${hexToRgb(theme.palette.primary.main)}, 0.1)`,
    }
});

export const getContactsFeedEmptyStateStyles = (theme) => ({
    textAlign: 'center',
    py: 3,
    px: 2,
    borderRadius: 1,
    bgcolor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.01)'
});

export const getTodayShellStyles = () => ({
    py: { xs: 2, sm: 3 },
    px: { xs: 2, sm: 3 },
    pb: { xs: 10, sm: 4 }
});

export const getTodayHeaderStyles = () => ({
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
    gap: 2,
    alignItems: 'end',
    mb: 3
});

export const getTodayTitleStyles = (theme) => ({
    fontSize: { xs: '1.6rem', sm: '2rem' },
    fontWeight: 750,
    color: theme.palette.text.primary,
    letterSpacing: 0,
    lineHeight: 1.15,
    mb: 1
});

export const getTodaySubtitleStyles = (theme) => ({
    color: theme.palette.text.secondary,
    maxWidth: 620,
    mx: 'auto',
    textAlign: 'center',
    lineHeight: 1.6
});

export const getTodayModeBarStyles = (theme) => ({
    display: 'flex',
    gap: 1,
    flexWrap: 'wrap',
    justifyContent: { xs: 'flex-start', md: 'flex-end' },
    p: 0.75,
    border: `1px solid ${theme.palette.mode === 'dark'
        ? `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.35)`
        : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.18)`}`,
    borderRadius: '8px',
    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.025)' : 'rgba(255,255,255,0.75)',
    backgroundImage: theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.12), rgba(${hexToRgb(theme.palette.secondary.main)}, 0.1))`
        : `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.06), rgba(${hexToRgb(theme.palette.secondary.main)}, 0.06))`,
});

export const getTodayCardsGridStyles = (compact = false) => ({
    display: 'grid',
    gridTemplateColumns: compact
        ? { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }
        : 'minmax(0, 760px)',
    gap: { xs: 1.5, sm: 2 },
    alignItems: 'stretch',
    justifyContent: 'center'
});

export const getTodayCardStyles = (theme, needsDecision = false, featured = false) => ({
    height: '100%',
    minHeight: featured ? 420 : 220,
    p: { xs: 2, sm: featured ? 3 : 2 },
    borderRadius: '8px',
    border: `1px solid ${needsDecision
        ? theme.palette.warning.main
        : featured
            ? (theme.palette.mode === 'dark'
                ? `rgba(${hexToRgb(theme.palette.primary.main)}, 0.34)`
                : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.24)`)
            : theme.palette.divider}`,
    bgcolor: theme.palette.background.paper,
    backgroundImage: featured
        ? (theme.palette.mode === 'dark'
            ? `linear-gradient(145deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.13) 0%, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.1) 48%, rgba(${hexToRgb(theme.palette.info.main)}, 0.07) 100%)`
            : `linear-gradient(145deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.08) 0%, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.08) 52%, rgba(${hexToRgb(theme.palette.info.main)}, 0.06) 100%)`)
        : 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: featured ? 2 : 1.25,
    cursor: featured ? 'default' : 'pointer',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: featured
        ? (theme.palette.mode === 'dark'
            ? `0 18px 48px rgba(0,0,0,0.32), 0 0 0 1px rgba(${hexToRgb(theme.palette.secondary.main)}, 0.08) inset`
            : `0 18px 42px rgba(${hexToRgb(theme.palette.primary.main)}, 0.12)`)
        : 'none',
    '&::before': featured ? {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
    } : undefined,
});

export const getTodayDraftStyles = (theme) => ({
    p: 1.5,
    borderRadius: '8px',
    border: `1px solid ${theme.palette.mode === 'dark'
        ? `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.34)`
        : `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.26)`}`,
    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.025)',
    backgroundImage: theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.14), rgba(${hexToRgb(theme.palette.primary.main)}, 0.08))`
        : `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.11), rgba(${hexToRgb(theme.palette.primary.main)}, 0.05))`,
    color: theme.palette.text.primary,
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap'
});

export const getTodayActionRowStyles = () => ({
    display: 'grid',
    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, minmax(0, 1fr))' },
    gap: 1,
    mt: 'auto'
});

// ============================================================================
// CONTACTS PAGE STYLES
// ============================================================================

export const getContactsPageHeaderStyles = () => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'stretch', sm: 'center' },
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 2
});

export const getContactsPageTabsStyles = (theme) => ({
    borderBottom: `2px solid ${theme.palette.mode === 'dark'
        ? `rgba(${hexToRgb(theme.palette.primary.main)}, 0.2)`
        : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.15)`}`,
    mb: 3,
    '& .MuiTab-root': {
        minHeight: { xs: 48, sm: 64 },
        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
        fontWeight: 500,
        textTransform: 'none',
        color: theme.palette.text.secondary,
        '&.Mui-selected': {
            color: theme.palette.primary.main,
            fontWeight: 600
        }
    },
    '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.primary.main,
        height: 3
    }
});

// ============================================================================
// CONTACT CARD STYLES
// ============================================================================

export const getContactCardStyles = (theme) => ({
    p: { xs: 2.5, sm: 3 },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 200,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '14px',
    bgcolor: theme.palette.background.paper,
    backgroundImage: 'none',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 1px 3px rgba(0, 0, 0, 0.2)'
        : '0 1px 3px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        borderColor: theme.palette.primary.light,
        transform: 'translateY(-3px)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 24px rgba(0, 0, 0, 0.3)'
            : `0 8px 24px rgba(${hexToRgb(theme.palette.primary.main)}, 0.12)`,
    }
});

export const getContactCardHeaderStyles = () => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    mb: 1.5
});

export const getContactCardNameStyles = (theme) => ({
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    mb: 0.5,
    lineHeight: 1.4,
    letterSpacing: '-0.005em',
});

export const getContactCardSubtitleStyles = (theme) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5,
});

export const getContactCardTraitStyles = (theme) => ({
    mb: 1.5,
    backgroundColor: theme.palette.mode === 'dark'
        ? `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.08)`
        : `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.05)`,
    borderRadius: 1,
    p: 1.5,
    border: `1px solid ${theme.palette.mode === 'dark' 
        ? `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.15)` 
        : `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.1)`}`
});

export const getContactCardTraitTextStyles = (theme) => ({
    color: theme.palette.text.primary,
    fontSize: '0.8125rem',
    fontStyle: 'italic',
    lineHeight: 1.5
});

export const getContactCardTagStyles = (theme) => ({
    fontSize: '0.75rem',
    height: 24,
    minHeight: 24,
    maxWidth: 120,
    borderRadius: '6px',
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
    fontWeight: 500,
    transition: 'all 0.15s ease',
    '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.main,
    }
});

export const getContactCardTagMoreStyles = (theme) => ({
    fontSize: '0.8125rem',
    height: 28,
    minHeight: 28,
    borderColor: theme.palette.divider,
    color: theme.palette.text.secondary
});

export const getContactCardFlex1BoxStyles = () => ({
    flex: 1
});

export const getContactCardTagsBoxStyles = () => ({
    display: 'flex',
    gap: 1,
    flexWrap: 'wrap'
});

// ============================================================================
// CONTACT GRID STYLES
// ============================================================================

export const getContactGridContainerStyles = () => ({
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        md: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(3, minmax(0, 1fr))',
    },
    gap: { xs: 1.25, sm: 1.5 },
    overflow: 'hidden',
    width: '100%',
    alignItems: 'stretch',
});

export const getContactGridLoadingStyles = (theme) => ({
    width: 40,
    height: 40,
    border: `2px solid ${theme.palette.divider}`,
    borderTop: `2px solid ${theme.palette.primary.main}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
    '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
    }
});

export const getContactGridEmptyStyles = (theme) => ({
    textAlign: 'center',
    py: 8,
    px: 2
});

export const getContactGridWrapperBoxStyles = () => ({
    width: '100%',
    mt: 2.5
});
export const getContactGridItemBoxStyles = () => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
});

export const getContactGridLoadingContainerStyles = () => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    py: 4
});

export const getContactGridLoadingInnerBoxStyles = () => ({
    textAlign: 'center'
});

export const getContactGridLoadingTextStyles = (theme) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.875rem'
});

export const getContactGridEmptyTitleStyles = (theme) => ({
    mb: 2,
    color: theme.palette.text.secondary
});

export const getContactGridEmptySubtitleStyles = getTextSecondaryColorStyles;

export const getContactGridLoadMoreBoxStyles = () => ({
    display: 'flex',
    justifyContent: 'center',
    py: 2
});

export const getContactGridLoadMoreButtonStyles = () => ({
    textTransform: 'none'
});

// ============================================================================
// CONTACT DETAILS DIALOG STYLES
// ============================================================================

export const getDialogStyles = (theme) => ({
    borderRadius: '14px',
    border: `1px solid ${theme.palette.mode === 'dark'
        ? `rgba(${hexToRgb(theme.palette.primary.main)}, 0.25)`
        : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.15)`}`,
    backgroundImage: theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.08) 0%, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.04) 100%)`
        : `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.06) 0%, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.03) 100%)`,
    boxShadow: theme.palette.mode === 'dark'
        ? '0 24px 48px rgba(0, 0, 0, 0.5)'
        : `0 24px 48px rgba(${hexToRgb(theme.palette.primary.main)}, 0.12)`,
    overflow: 'hidden',
    position: 'relative',
    // Декоративный акцент
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '4px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        zIndex: 1000
    }
});

export const getDialogTitleStyles = (theme) => ({
    borderBottom: `2px solid ${theme.palette.mode === 'dark'
        ? `rgba(${hexToRgb(theme.palette.primary.main)}, 0.2)`
        : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.15)`}`,
    pb: 1.75,
    pt: 2.5,
    px: { xs: 2, sm: 3 },
    background: theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.1) 0%, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.05) 100%)`
        : `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.primary.main)}, 0.06) 0%, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.03) 100%)`
});

export const getTitleContainerStyles = () => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 2
});

export const getTitleNameContainerStyles = () => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flex: 1
});

export const getTitleNameStyles = (theme) => ({
    fontSize: { xs: '1rem', sm: '1.05rem' },
    lineHeight: 1.35,
    fontWeight: 700,
    letterSpacing: 0,
    color: theme.palette.text.primary,
});

export const getTitleActionsContainerStyles = () => ({
    display: 'flex',
    gap: 0.5
});

export const getIconButtonStyles = (theme) => ({
    color: theme.palette.text.secondary,
    '&:hover': {
        color: theme.palette.primary.main,
        bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.04)'
    }
});

export const getDeleteIconButtonStyles = (theme) => ({
    color: theme.palette.error.main,
    '&:hover': {
        bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(211, 47, 47, 0.16)'
            : 'rgba(211, 47, 47, 0.08)'
    }
});

export const getCloseIconButtonStyles = (theme) => ({
    color: theme.palette.text.secondary,
    '&:hover': {
        color: theme.palette.text.primary,
        bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.04)'
    }
});

export const getDialogContentStyles = () => ({
    px: { xs: 2, sm: 3 },
    py: 2.5
});

export const getContentContainerStyles = () => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 0
});

export const getSectionContainerStyles = () => ({
    py: 1.65
});

export const getDividerStyles = () => ({
    my: 0,
    opacity: 0.72
});

// ============================================================================
// BOTTOM NAVIGATION STYLES
// ============================================================================

export const getBottomNavigationContainerStyles = (theme) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    borderTop: theme.palette.isGlassmorphism
        ? (theme.palette.mode === 'light' ? '0.5px solid rgba(255, 255, 255, 0.6)' : '0.5px solid rgba(255, 255, 255, 0.1)')
        : `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.isGlassmorphism
        ? (theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.06)')
        : theme.palette.background.paper,
    backdropFilter: theme.palette.isGlassmorphism ? 'blur(40px) saturate(1.8)' : 'none',
    WebkitBackdropFilter: theme.palette.isGlassmorphism ? 'blur(40px) saturate(1.8)' : 'none',
    boxShadow: theme.palette.isGlassmorphism
        ? (theme.palette.mode === 'dark' ? '0 -2px 16px rgba(0, 0, 0, 0.3)' : '0 -2px 16px rgba(0, 0, 0, 0.04)')
        : (theme.palette.mode === 'dark'
            ? '0 -2px 8px rgba(0, 0, 0, 0.5)'
            : '0 -2px 8px rgba(0, 0, 0, 0.1)'),
    paddingBottom: 'env(safe-area-inset-bottom)',
});

export const getBottomNavigationStyles = (theme) => ({
    height: 64,
    width: '100%',
    backgroundColor: 'transparent',
    '& .MuiBottomNavigationAction-root': {
        color: theme.palette.text.secondary,
        minWidth: 'auto',
        padding: '6px 12px',
        '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-selected': {
            color: theme.palette.primary.main,
            '& .MuiBottomNavigationAction-label': {
                fontWeight: 600,
            },
        },
        '& .MuiBottomNavigationAction-label': {
            fontSize: '0.8125rem',
            marginTop: '6px',
            transition: 'font-weight 0.2s ease',
            '&.Mui-selected': {
                fontSize: '0.8125rem',
            },
        },
        '& .MuiSvgIcon-root': {
            fontSize: '1.17rem',   // 1.75 / 1.5 ≈ 1.17
        },
    },
});

export const getMainContentWithBottomNavStyles = (hasBottomNav) => ({
    paddingBottom: hasBottomNav 
        ? { xs: 'calc(64px + env(safe-area-inset-bottom) + 16px)', sm: 'calc(64px + 16px)', md: 'calc(64px + 16px)' } 
        : 2,
});

// ============================================================================
// ACCOUNT PAGE STYLES
// ============================================================================

export const getAccountTabsStyles = (theme) => ({
    borderBottom: `2px solid ${theme.palette.mode === 'dark'
        ? `rgba(${hexToRgb(theme.palette.primary.main)}, 0.2)`
        : `rgba(${hexToRgb(theme.palette.primary.main)}, 0.15)`}`,
    mb: 3,
    '& .MuiTab-root': {
        minHeight: { xs: 48, sm: 64 },
        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
        fontWeight: 500,
        textTransform: 'none',
        color: theme.palette.text.secondary,
        '&.Mui-selected': {
            color: theme.palette.primary.main,
            fontWeight: 600
        }
    },
    '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.primary.main,
        height: 3
    }
});

// ============================================================================
// PROFILE PAGE STYLES
// ============================================================================

export const getProfilePaperStyles = (theme) => ({
    p: { xs: 2, sm: 3 },
    mb: 3,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    bgcolor: theme.palette.background.paper
});

export const getProfileTabsStyles = (theme) => ({
    mb: 2,
    borderBottom: `1px solid ${theme.palette.divider}`
});

export const getProfileDangerZoneStyles = (theme) => ({
    p: { xs: 2, sm: 3 },
    mt: 3,
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: 2,
    bgcolor: theme.palette.mode === 'dark' 
        ? 'rgba(211, 47, 47, 0.1)'
        : 'rgba(211, 47, 47, 0.05)'
});

// ============================================================================
// CHAT PAGE STYLES
// ============================================================================

export const getChatContainerStyles = () => ({
    p: 0,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column'
});

export const getChatBoxStyles = (theme) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    bgcolor: theme.palette.background.default,
    overflow: 'hidden'
});

export const getChatAppBarStyles = (theme) => ({
    bgcolor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px 8px 0 0'
});

export const getChatToolbarStyles = () => ({
    minHeight: { xs: '56px', sm: '60px' } + ' !important',
    px: { xs: 1, sm: 3 },
    py: { xs: 0.5, sm: 0 },
    gap: { xs: 1, sm: 2 }
});

export const getChatAvatarStyles = () => ({
    bgcolor: '#10a37f',
    mr: { xs: 0.5, sm: 2 },
    width: { xs: 28, sm: 36 },
    height: { xs: 28, sm: 36 }
});

export const getChatTitleStyles = (theme) => ({
    color: theme.palette.text.primary,
    fontSize: { xs: '0.9375rem', sm: '1.125rem' },
    fontWeight: 600,
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

export const getChatCloseButtonStyles = (theme) => ({
    color: theme.palette.text.secondary,
    padding: { xs: '4px', sm: '8px' },
    minWidth: { xs: 32, sm: 40 },
    minHeight: { xs: 32, sm: 40 },
    '&:hover': {
        color: theme.palette.text.primary,
        bgcolor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.08)' 
            : 'rgba(0, 0, 0, 0.04)'
    }
});

export const getChatListStyles = (theme) => ({
    flexGrow: 1,
    overflow: 'auto',
    p: 0,
    bgcolor: theme.palette.background.default,
    '& .MuiListItem-root': {
        padding: { xs: '12px 16px', sm: '20px 24px' },
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none'
        }
    }
});

export const getChatMessageItemStyles = (theme, sender) => ({
    flexDirection: sender === 'user' ? 'row-reverse' : 'row',
    position: 'relative',
    zIndex: 1,
    bgcolor: sender === 'user' 
        ? (theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)' 
            : 'rgba(0, 0, 0, 0.02)')
        : 'transparent'
});

export const getChatInputContainerStyles = (theme) => ({
    p: { xs: 1.5, sm: 2 },
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 1, sm: 1.5 },
    bgcolor: theme.palette.background.paper,
    position: 'relative',
    zIndex: 1000,
    minHeight: { xs: 60, sm: 72 }
});

export const getChatTextFieldStyles = (theme) => ({
    '& .MuiOutlinedInput-root': {
        bgcolor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)' 
            : 'rgba(0, 0, 0, 0.02)',
        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
        borderRadius: 2,
        '& fieldset': {
            borderColor: theme.palette.divider,
            borderWidth: '1px'
        },
        '&:hover fieldset': {
            borderColor: theme.palette.primary.main
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px'
        },
        '& .MuiInputBase-input': {
            padding: { xs: '10px 14px', sm: '12px 16px' },
            fontSize: { xs: '0.875rem', sm: '0.9375rem' }
        }
    },
    '& .MuiInputBase-input': {
        color: theme.palette.text.primary,
        '&::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1
        }
    }
});

export const getChatSendButtonStyles = (theme) => ({
    color: theme.palette.primary.main,
    padding: { xs: '8px', sm: '10px' },
    bgcolor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.05)' 
        : 'rgba(0, 0, 0, 0.02)',
    borderRadius: 2,
    '& .MuiSvgIcon-root': {
        fontSize: { xs: 18, sm: 20 }
    },
    '&:hover': {
        bgcolor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.08)' 
            : 'rgba(0, 0, 0, 0.04)'
    },
    '&:disabled': {
        color: theme.palette.text.disabled
    },
    minWidth: { xs: 40, sm: 44 },
    minHeight: { xs: 40, sm: 44 }
});

// ============================================================================
// MAPS PAGE STYLES
// ============================================================================

export const getMapsHeaderStyles = (theme) => ({
    p: { xs: 2, sm: 3 },
    mb: 3,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    bgcolor: theme.palette.background.paper
});

export const getMapsHeaderTitleStyles = (theme) => ({
    fontWeight: 600,
    color: theme.palette.text.primary
});

export const getMapsMapContainerStyles = (theme) => ({
    height: '100%',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2
});

// ============================================================================
// ADDITIONAL COMMON STYLES
// ============================================================================

export const getFlexColumnGap25Styles = getFlexColumnGap25BaseStyles;
export const getFlexRowGap1Styles = getFlexRowGap1BaseStyles;
export const getFlexWrapGap1Styles = getFlexWrapGap1BaseStyles;

export const getFlexColumnGap3Styles = () => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 3
});



export const getFlexRowGap15Styles = () => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5
});


export const getSectionTitleWithIconStyles = (theme) => ({
    fontWeight: 600,
    fontSize: '0.9375rem',
    color: theme.palette.text.primary,
    mb: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5
});

export const getSectionTitleStyles = (theme) => ({
    fontWeight: 600,
    color: theme.palette.text.primary,
    mb: 1
});

export const getBody2Pl3Styles = (theme) => ({
    color: theme.palette.text.primary,
    pl: 3,
    fontSize: '0.9375rem',
    lineHeight: 1.6
});

export const getContactsFeedInteractionDateStyles = (theme) => ({
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem'
});

export const getContactsFeedInteractionDaysStyles = (theme) => ({
    fontWeight: 400,
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem'
});

export const getContactsFeedInteractionNotesStyles = (theme) => ({
    color: theme.palette.text.primary,
    fontSize: '0.9375rem',
    lineHeight: 1.6
});

export const getContactsFeedInteractionNotesEmptyStyles = (theme) => ({
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '0.9375rem'
});

export const getContactsPageButtonsContainerStyles = () => ({
    display: 'flex',
    gap: 1,
    flexDirection: 'row',
    width: { xs: '100%', sm: 'auto' },
    alignItems: 'center',
    justifyContent: { xs: 'stretch', sm: 'flex-end' },
    flexWrap: { xs: 'wrap', sm: 'nowrap' }
});

export const getContactsPageButtonStyles = () => ({
    flex: { xs: '1 1 140px', sm: 'none' },
    minHeight: 36,
    borderRadius: '8px',
    textTransform: 'none',
    whiteSpace: 'nowrap'
});

export const getContactsPageToolbarStyles = (theme) => ({
    pb: 2,
    mb: 0,
    borderBottom: `1px solid ${theme.palette.divider}`
});

export const getContactsPageTagsRowStyles = () => ({
    display: 'flex',
    gap: 0.75,
    flexWrap: 'wrap',
    mt: 2,
    alignItems: 'center',
    overflow: 'hidden',
    maxWidth: '100%'
});

export const getContactsPageTabsContainerStyles = () => ({
    display: 'flex',
    justifyContent: 'center',
    mb: 3
});

export const getContactsPageTabsPaperStyles = (theme) => ({
    borderRadius: 2,
    overflow: 'hidden',
    display: 'inline-block',
    border: `1px solid ${theme.palette.divider}`,
    bgcolor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.01)'
});

export const getContactsPageFilterPaperStyles = (theme) => ({
    p: 2,
    mb: 3,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    bgcolor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.01)'
});

export const getContactsPageFilterContainerStyles = () => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 2
});

export const getContactsPageFilterCountStyles = (theme) => ({
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    mb: 0.5
});

export const getContactsPageFilterLabelStyles = (theme) => ({
    fontStyle: 'italic',
    fontSize: '0.8125rem',
    color: theme.palette.secondary.main,
    fontWeight: 500
});

export const getContactsPageTabLabelBoxStyles = getFlexRowGap1BaseStyles;

export const getContactsPageFormControlStyles = () => ({
    minWidth: { xs: '100%', sm: 140 },
    width: { xs: '100%', sm: 'auto' }
});

export const getContactsPageMapInfoBoxStyles = getMarginTop1Styles;

export const getContactsPageMapInfoPaperStyles = (theme) => ({
    p: 2,
    mb: 2,
    border: `1px solid ${theme.palette.mode === 'dark'
        ? `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.3)`
        : `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.2)`}`,
    borderRadius: 2,
    bgcolor: theme.palette.mode === 'dark' 
        ? `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.12)`
        : `rgba(${hexToRgb(theme.palette.secondary.main)}, 0.08)`,
    backgroundImage: theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.15) 0%, rgba(${hexToRgb(theme.palette.primary.main)}, 0.08) 100%)`
        : `linear-gradient(135deg, rgba(${hexToRgb(theme.palette.secondary.main)}, 0.1) 0%, rgba(${hexToRgb(theme.palette.primary.main)}, 0.05) 100%)`
});

export const getContactsPageMapInfoInnerBoxStyles = () => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
});

export const getContactsPageMapContainerStyles = (theme) => ({
    height: '60vh',
    borderRadius: 2,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`
});

export const getMapsHeaderIconBoxStyles = () => ({
    display: 'flex',
    alignItems: 'center',
    mb: 2
});

export const getMapsMapBoxStyles = () => ({
    height: '60vh'
});

export const getMapsPublicIconStyles = (theme) => ({
    mr: 1.5,
    color: theme.palette.primary.main,
    fontSize: 28
});

export const getMapsHeaderChipsBoxDynamicStyles = (hasContacts) => ({
    display: 'flex',
    gap: 1.5,
    alignItems: 'center',
    flexWrap: 'wrap',
    mb: hasContacts ? 0 : 2
});

export const getMapsAlertStyles = () => ({
    mt: 2
});

// ============================================================================
// ALERT STYLES
// ============================================================================

export const getAlertMb2Styles = getMarginBottom2Styles;

// ============================================================================
// CONTACTS FEED ADDITIONAL STYLES
// ============================================================================

export const getContactsFeedCardHeaderBoxStyles = getMarginBottom2Styles;

export const getContactsFeedCardHeaderInnerBoxStyles = () => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 1
});

export const getContactsFeedCardContentBoxStyles = getFlexColumnGap25BaseStyles;

export const getContactsFeedDatesListStyles = () => ({
    pl: 1
});

export const getContactsFeedDateItemStyles = () => ({
    py: 0.5
});

export const getContactsFeedDateIconStyles = () => ({
    minWidth: 32
});

export const getContactsFeedTagsBoxStyles = getFlexWrapGap1BaseStyles;

export const getContactsFeedInteractionsListStyles = () => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5
});

export const getContactsFeedInteractionDateBoxStyles = () => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    mb: 1.5
});

export const getContactsFeedInteractionTimeBoxStyles = () => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.5
});

export const getContactsFeedInteractionTimeIconStyles = () => ({
    fontSize: 16
});

export const getContactsFeedInteractionActionsBoxStyles = () => ({
    display: 'flex',
    gap: 0.5,
    mt: 1
});

export const getContactsFeedEmptyIconStyles = (theme) => ({
    fontSize: 40,
    color: theme.palette.text.secondary,
    mb: 1
});

export const getContactsFeedEmptyTitleStyles = getTitleMediumStyles;

export const getContactsFeedEmptySubtitleStyles = (theme) => ({
    color: theme.palette.text.secondary,
    mb: 2
});

export const getContactsFeedMoreConversationsBoxStyles = getMarginTop1Styles;

export const getContactsFeedMoreConversationsTextStyles = (theme) => ({
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '0.875rem'
});

export const getContactsFeedAddInteractionBoxStyles = (theme) => ({
    mt: 3,
    pt: 2.5,
    borderTop: `1px dashed ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'center'
});

export const getContactsFeedLoadMoreBoxStyles = () => ({
    mt: 3
});

export const getContactsFeedLoadMoreButtonStyles = () => ({
    py: 1.5
});

export const getContactsFeedEndMessageBoxStyles = (theme) => ({
    mt: 4,
    textAlign: 'center',
    py: 3,
    px: 2,
    borderRadius: 1,
    bgcolor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.01)'
});

export const getContactsFeedEndMessageTitleStyles = getTitleMediumStyles;

export const getContactsFeedEndMessageSubtitleStyles = getTextSecondaryColorStyles;

// ============================================================================
// ACCOUNT ADDITIONAL STYLES
// ============================================================================

export const getAccountPaperWithMarginStyles = (theme) => ({
    ...getPaperStyles(theme),
    mb: 3
});

// ============================================================================
// WARMTH GRID STYLES (Gentle Pulse Dashboard)
// ============================================================================

export const getGreetingStyles = (theme) => ({
    fontWeight: 700,
    fontSize: { xs: '1.25rem', sm: '1.5rem' },
    color: theme.palette.text.primary,
    letterSpacing: 0,
});

export const getFeedSearchStyles = (theme) => ({
    width: { xs: '100%', sm: 320, md: 360 },
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.03)',
        fontSize: '0.875rem',
        '& fieldset': {
            borderColor: theme.palette.divider,
        },
        '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
        },
    },
});

export const getWarmthGridContainerStyles = (columns) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    gap: { xs: '5px', sm: '6px', md: '7px' },
    width: '100%',
    maxWidth: {
        xs: '100%',
        sm: `${Math.max(columns * 42, 240)}px`,
        md: `${Math.max(columns * 52, 300)}px`,
    },
    mx: 'auto',
    perspective: '800px',
    justifyContent: 'center',
});

/**
 * Interpolates warmth value to a color string.
 * Emerald-teal palette: bright teal → deep green → dark grey.
 */
const warmthToColor = (warmth) => {
    if (warmth >= 0.7) {
        // Bright teal #2DD4BF → emerald #10B981
        const t = (warmth - 0.7) / 0.3;
        const r = Math.round(16 + (45 - 16) * t);    // 10 → 2D
        const g = Math.round(185 + (212 - 185) * t);  // B9 → D4
        const b = Math.round(129 + (191 - 129) * t);  // 81 → BF
        return `rgb(${r}, ${g}, ${b})`;
    }
    if (warmth >= 0.4) {
        // Emerald #10B981 → deep teal #0D5E4A
        const t = (warmth - 0.4) / 0.3;
        const r = Math.round(13 + (16 - 13) * t);     // 0D → 10
        const g = Math.round(94 + (185 - 94) * t);    // 5E → B9
        const b = Math.round(74 + (129 - 74) * t);    // 4A → 81
        return `rgb(${r}, ${g}, ${b})`;
    }
    if (warmth > 0) {
        // Dark grey #1E2A2A → deep teal #0D5E4A
        const t = warmth / 0.4;
        const r = Math.round(30 + (13 - 30) * t);     // 1E → 0D
        const g = Math.round(42 + (94 - 42) * t);     // 2A → 5E
        const b = Math.round(42 + (74 - 42) * t);     // 2A → 4A
        return `rgb(${r}, ${g}, ${b})`;
    }
    return 'rgb(26, 32, 32)'; // #1a2020 cold dark with slight teal tint
};

export const getWarmthGridSquareStyles = (warmth, delay, durationOffset, theme, isPlaceholder = false) => {
    const bgColor = warmthToColor(warmth);

    const duration = (8 + durationOffset * 6) / 3.5; // ~2.3-4s per square
    const delayS = (delay * 5) / 3.5; // 0-1.4s stagger

    const isHot = warmth >= 0.7;
    const isWarm = warmth >= 0.4;
    const isCool = warmth > 0 && !isWarm;

    const glowColor = isHot
        ? 'rgba(45, 212, 191, 0.35)'
        : 'rgba(16, 185, 129, 0.2)';

    // 3D pop height depends on warmth
    const popZ = isHot ? 14 : isWarm ? 8 : 4;
    const animName = isHot ? 'cubePop3dHot' : isWarm ? 'cubePop3dWarm' : 'cubePop3dCool';

    return {
        width: '100%',
        aspectRatio: '1',
        borderRadius: '4px',
        cursor: isPlaceholder ? 'default' : 'pointer',
        backgroundColor: bgColor,
        opacity: isPlaceholder ? 0.14 : 1,
        pointerEvents: isPlaceholder ? 'none' : 'auto',
        transformStyle: 'preserve-3d',
        transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease, border-radius 0.2s ease',

        // 3D pop animation — tiles rise and sink back
        ...(warmth > 0 && {
            animation: `${animName} ${duration}s ease-in-out ${delayS}s infinite`,
        }),

        // Resting glow for hot tiles
        ...(isHot && {
            boxShadow: `0 0 8px ${glowColor}`,
        }),

        // Hot: slow ocean swell with glow
        '@keyframes cubePop3dHot': {
            '0%, 100%': {
                transform: 'perspective(600px) translateZ(0) rotateX(0) rotateY(0)',
                boxShadow: `0 0 6px ${glowColor}`,
            },
            '35%': {
                transform: `perspective(600px) translateZ(${popZ}px) rotateX(-1deg) rotateY(0.8deg)`,
                boxShadow: `0 4px 16px ${glowColor}`,
            },
            '65%': {
                transform: `perspective(600px) translateZ(${popZ * 0.3}px) rotateX(0.3deg) rotateY(-0.2deg)`,
                boxShadow: `0 1px 8px ${glowColor}`,
            },
        },

        // Warm: gentle swell
        '@keyframes cubePop3dWarm': {
            '0%, 100%': {
                transform: 'perspective(600px) translateZ(0) rotateX(0) rotateY(0)',
                boxShadow: 'none',
            },
            '40%': {
                transform: `perspective(600px) translateZ(${popZ}px) rotateX(-0.8deg) rotateY(0.5deg)`,
                boxShadow: `0 3px 10px ${glowColor}`,
            },
        },

        // Cool: barely-there drift
        '@keyframes cubePop3dCool': {
            '0%, 100%': {
                transform: 'perspective(600px) translateZ(0) rotateX(0) rotateY(0)',
            },
            '45%': {
                transform: `perspective(600px) translateZ(${popZ}px) rotateX(-0.4deg) rotateY(0.3deg)`,
            },
        },

        '&:hover': {
            transform: 'perspective(600px) translateZ(24px) rotateX(-3deg) rotateY(2deg) scale(1.12)',
            zIndex: 2,
            borderRadius: '6px',
            boxShadow: warmth > 0.3
                ? `0 8px 24px ${glowColor}, 0 0 16px ${glowColor}`
                : '0 6px 16px rgba(255,255,255,0.08)',
        },
    };
};
